import { useState, useCallback } from "react";
import { useSpeciesList } from "@/hooks";
import {
  settingsSummary,
  botanicalFamilies,
  riskCriteriaList,
  riskMatrix,
  managementTypes,
  inspectionTypes,
  userProfiles,
  usersList,
  clients,
  municipalities,
  integrations,
  auditLogs,
} from "@/mocks/configuracoes";
import type { Species, AuditLog } from "@/mocks/configuracoes";
import SettingsKpiCard from "./components/SettingsKpiCard";
import SettingsStatusBadge from "./components/SettingsStatusBadge";
import GeneralSettingsPanel from "./components/GeneralSettingsPanel";
import CtaDefaultsPanel from "./components/CtaDefaultsPanel";
import SpeciesTable from "./components/SpeciesTable";
import FamilyTable from "./components/FamilyTable";
import NewSpeciesModal from "./components/NewSpeciesModal";
import RiskCriteriaTable from "./components/RiskCriteriaTable";
import RiskMatrixPanel from "./components/RiskMatrixPanel";
import ManagementTypesTable from "./components/ManagementTypesTable";
import InspectionTypesTable from "./components/InspectionTypesTable";
import UsersTable from "./components/UsersTable";
import PermissionsMatrix from "./components/PermissionsMatrix";
import NewUserModal from "./components/NewUserModal";
import ClientsTable from "./components/ClientsTable";
import MunicipalitiesTable from "./components/MunicipalitiesTable";
import GisSettingsPanel from "./components/GisSettingsPanel";
import GisValidationPanel from "./components/GisValidationPanel";
import IntegrationsGrid from "./components/IntegrationsGrid";
import AppearanceSettingsPanel from "./components/AppearanceSettingsPanel";
import MenuVisibilityPanel from "./components/MenuVisibilityPanel";
import SystemAuditTable from "./components/SystemAuditTable";
import AuditDetailDrawer from "./components/AuditDetailDrawer";

const tabs = [
  { id: "geral", label: "Geral" },
  { id: "especies", label: "Espécies e Botânica" },
  { id: "risco", label: "Critérios de Risco" },
  { id: "manejo", label: "Manejo e Vistorias" },
  { id: "usuarios", label: "Usuários e Permissões" },
  { id: "clientes", label: "Clientes e Municípios" },
  { id: "gis", label: "GIS e Coordenadas" },
  { id: "integracoes", label: "Integrações" },
  { id: "menu", label: "Menu e Navegação" },
  { id: "aparencia", label: "Aparência e Sistema" },
  { id: "auditoria", label: "Auditoria" },
];

export default function Configuracoes() {
  const [activeTab, setActiveTab] = useState("geral");
  const [showNewSpecies, setShowNewSpecies] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const { items: speciesList } = useSpeciesList();

  const handleEditSpecies = useCallback((_s: Species) => {
    setShowNewSpecies(true);
  }, []);

  const handleViewLogDetail = useCallback((log: AuditLog) => setSelectedLog(log), []);
  const handleCloseLogDetail = useCallback(() => setSelectedLog(null), []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Configurações</div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            Configurações
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Gerencie parâmetros técnicos, cadastros auxiliares, usuários, permissões, espécies, critérios de risco e padrões do CTA.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setActiveTab("especies")} className="px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
            <i className="ri-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Nova configuração
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-upload-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Importar cadastros
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Exportar configurações
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-save-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Salvar alterações
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-restart-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Restaurar padrão
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <SettingsKpiCard label="Espécies cadastradas" value={String(settingsSummary.totalSpecies)} icon="ri-plant-line" color="primary" subtitle="Catálogo botânico" />
        <SettingsKpiCard label="Famílias botânicas" value={String(settingsSummary.totalFamilies)} icon="ri-stack-line" color="primary" subtitle="Taxonomia" />
        <SettingsKpiCard label="Usuários ativos" value={String(settingsSummary.activeUsers)} icon="ri-team-line" color="accent" subtitle="Acessos à plataforma" />
        <SettingsKpiCard label="Perfis de acesso" value={String(settingsSummary.accessProfiles)} icon="ri-shield-keyhole-line" color="accent" subtitle="Níveis de permissão" />
        <SettingsKpiCard label="Critérios de risco" value={String(settingsSummary.riskCriteria)} icon="ri-error-warning-line" color="warning" subtitle="Parâmetros de classificação" />
        <SettingsKpiCard label="Tipos de manejo" value={String(settingsSummary.managementTypes)} icon="ri-scissors-line" color="warning" subtitle="Intervenções cadastradas" />
        <SettingsKpiCard label="Municípios configurados" value={String(settingsSummary.configuredMunicipalities)} icon="ri-map-pin-line" color="secondary" subtitle="Com SRID definido" />
        <SettingsKpiCard label="Parâmetros ativos" value={String(settingsSummary.activeParameters)} icon="ri-settings-3-line" color="secondary" subtitle="Configurações salvas" />
      </div>

      {/* Conceptual notice */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-primary-600 text-lg" />
          </div>
          <p className="text-xs text-primary-700 leading-relaxed">
            As configurações definem os parâmetros técnicos e cadastros auxiliares que padronizam a operação do FloraIQ
            em todos os módulos: CTA, projetos, áreas verdes, carbono, documentos, partes e relatórios.
            Alterações aqui impactam toda a plataforma.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Geral */}
        {activeTab === "geral" && (
          <div className="space-y-5">
            <GeneralSettingsPanel />
            <CtaDefaultsPanel />
          </div>
        )}

        {/* Espécies e Botânica */}
        {activeTab === "especies" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Espécies cadastradas</h3>
              <button onClick={() => setShowNewSpecies(true)} className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
                <i className="ri-add-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                Nova espécie
              </button>
            </div>
            <SpeciesTable species={speciesList} onEdit={handleEditSpecies} />

            <div className="border-t border-background-200 pt-5 mt-2">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Famílias botânicas</h3>
              <FamilyTable families={botanicalFamilies} />
            </div>
          </div>
        )}

        {/* Critérios de Risco */}
        {activeTab === "risco" && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold text-foreground-900">Critérios de classificação de risco</h3>
            <RiskCriteriaTable criteria={riskCriteriaList} />
            <RiskMatrixPanel matrix={riskMatrix} />
          </div>
        )}

        {/* Manejo e Vistorias */}
        {activeTab === "manejo" && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Tipos de manejo</h3>
              <p className="text-xs text-foreground-500 mb-4">Configuração dos tipos de intervenção arbórea disponíveis</p>
              <ManagementTypesTable types={managementTypes} />
            </div>
            <div className="border-t border-background-200 pt-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Tipos de vistoria</h3>
              <p className="text-xs text-foreground-500 mb-4">Configuração dos tipos de vistoria e seus campos obrigatórios</p>
              <InspectionTypesTable types={inspectionTypes} />
            </div>
          </div>
        )}

        {/* Usuários e Permissões */}
        {activeTab === "usuarios" && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Usuários</h3>
              <p className="text-xs text-foreground-500 mb-4">Gerencie os usuários com acesso à plataforma</p>
              <UsersTable users={usersList} onNewUser={() => setShowNewUser(true)} />
            </div>
            <div className="border-t border-background-200 pt-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Matriz de permissões</h3>
              <p className="text-xs text-foreground-500 mb-4">Módulos acessíveis por perfil de usuário</p>
              <PermissionsMatrix profiles={userProfiles} />
            </div>
          </div>
        )}

        {/* Clientes e Municípios */}
        {activeTab === "clientes" && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Clientes e contratantes</h3>
              <p className="text-xs text-foreground-500 mb-4">Organizações que utilizam os serviços do FloraIQ</p>
              <ClientsTable clients={clients} />
            </div>
            <div className="border-t border-background-200 pt-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Municípios configurados</h3>
              <p className="text-xs text-foreground-500 mb-4">Municípios com parâmetros GIS e territoriais definidos</p>
              <MunicipalitiesTable municipalities={municipalities} />
            </div>
          </div>
        )}

        {/* GIS e Coordenadas */}
        {activeTab === "gis" && (
          <div className="space-y-5">
            <GisSettingsPanel />
            <GisValidationPanel />
          </div>
        )}

        {/* Integrações */}
        {activeTab === "integracoes" && (
          <IntegrationsGrid integrations={integrations} />
        )}

        {/* Menu e Navegação */}
        {activeTab === "menu" && <MenuVisibilityPanel />}

        {/* Aparência e Sistema */}
        {activeTab === "aparencia" && (
          <AppearanceSettingsPanel />
        )}

        {/* Auditoria */}
        {activeTab === "auditoria" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900">Logs de auditoria do sistema</h3>
            <SystemAuditTable logs={auditLogs} onViewDetail={handleViewLogDetail} />
          </div>
        )}
      </div>

      {/* Modals & Drawers */}
      {showNewSpecies && <NewSpeciesModal onClose={() => setShowNewSpecies(false)} />}
      {showNewUser && <NewUserModal profiles={userProfiles} onClose={() => setShowNewUser(false)} />}
      {selectedLog && <AuditDetailDrawer log={selectedLog} onClose={handleCloseLogDetail} />}
    </div>
  );
}